import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Container, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";

import { schedulingApi } from "../Components/schedulingApi";
import styles from "./LecturerAttendancePublic.module.scss";

export default function LecturerAttendanceOtpPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const timezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC", []);

  const [loading, setLoading] = useState(true);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [context, setContext] = useState(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");

  const loadContext = async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const data = await schedulingApi.getLecturerAttendanceContext(token, timezone);
      if (data?.error) {
        setContext(null);
        setError(data.error);
      } else {
        setContext(data);
        if (data?.otp?.verified) {
          navigate(`/lecturer-attendance/${token}/submit`, { replace: true });
        }
      }
    } catch (e) {
      setError(e?.message || "Failed to load link details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContext();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSendOtp = async () => {
    setSendingOtp(true);
    setError("");
    try {
      const data = await schedulingApi.sendLecturerAttendanceOtp(token);
      if (data?.error) {
        setError(data.error);
        return;
      }
      toast.success("OTP sent to your email.");
      await loadContext();
    } catch (e) {
      setError(e?.message || "Failed to send OTP.");
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setVerifyingOtp(true);
    setError("");
    try {
      const data = await schedulingApi.verifyLecturerAttendanceOtp(token, code.trim());
      if (data?.error) {
        setError(data.error);
        return;
      }
      toast.success("OTP verified.");
      navigate(`/lecturer-attendance/${token}/submit`);
    } catch (e) {
      setError(e?.message || "Failed to verify OTP.");
    } finally {
      setVerifyingOtp(false);
    }
  };

  return (
    <div className={styles.pageWrap}>
      <Container className={styles.shell}>
        <Card className={styles.heroCard}>
          <div className={styles.heroHeader}>
            <div>
              <h4 className={styles.heroTitle}>Verify Attendance Link</h4>
              <p className={styles.heroSub}>Secure one-time code verification for lecturer attendance.</p>
            </div>
            <span className={styles.metaBadge}>Public Access</span>
          </div>
          <div className={styles.contentGrid}>
            <Card className={styles.sectionCard}>
              <Card.Body className={styles.sectionCardBody}>
                {loading ? (
                  <div className="d-flex align-items-center gap-2">
                    <Spinner animation="border" size="sm" />
                    <span>Loading link details...</span>
                  </div>
                ) : context ? (
                  <>
                    <h5 className={styles.sectionTitle}>{context?.event_title || "Class"}</h5>
                    <p className={styles.muted}>Start time: <strong>{context?.start_local || "TBC"}</strong></p>
                    <p className={styles.muted} style={{ marginTop: 8 }}>
                      A one-time code will be sent to:
                    </p>
                    <div className={styles.emailPill}>{context?.lecturer?.email || "your email"}</div>
                    {context?.otp?.already_submitted ? (
                      <Alert variant="info" className={`mt-3 mb-0 ${styles.stateBanner}`}>
                        Attendance has already been submitted. This link is no longer available.
                      </Alert>
                    ) : (
                      <>
                        <div className={styles.otpActions}>
                          <Button
                            variant="outline-primary"
                            onClick={handleSendOtp}
                            disabled={sendingOtp}
                            className={styles.primaryBtn}
                          >
                            {sendingOtp ? <Spinner size="sm" animation="border" /> : "Send / Resend OTP"}
                          </Button>
                        </div>
                        <Form onSubmit={handleVerifyOtp} className="mt-3">
                          <Form.Group className="mb-3">
                            <Form.Label>OTP code</Form.Label>
                            <Form.Control
                              type="text"
                              autoComplete="one-time-code"
                              placeholder="Enter OTP"
                              value={code}
                              onChange={(e) => setCode(e.target.value)}
                            />
                          </Form.Group>
                          <Button type="submit" disabled={!code.trim() || verifyingOtp} className={styles.primaryBtn}>
                            {verifyingOtp ? <Spinner size="sm" animation="border" /> : "Submit OTP"}
                          </Button>
                        </Form>
                      </>
                    )}
                  </>
                ) : (
                  <Alert variant="danger" className="mb-0">
                    {error || "Invalid or expired link."}
                  </Alert>
                )}
                {error && context ? (
                  <Alert variant="danger" className="mt-3 mb-0">
                    {error}
                  </Alert>
                ) : null}
              </Card.Body>
            </Card>

            <Card className={styles.sectionCard}>
              <Card.Body className={styles.sectionCardBody}>
                <h6 className={styles.sectionTitle}>How this works</h6>
                <p className={styles.muted}>1. Request or resend OTP to your email.</p>
                <p className={styles.muted}>2. Enter code and submit OTP.</p>
                <p className={styles.muted}>3. You will be redirected to submit class attendance.</p>
              </Card.Body>
            </Card>
          </div>
        </Card>
      </Container>
    </div>
  );
}
